/**
 * useCarousels.js — saved carousel layouts (the `carousels` table).
 *
 * Rows hold a slide layout, never images: `data` is the composer's own blob from
 * lib/carousel/persist.js and `source` says which HDPhotoHub shoots it needs to
 * reopen. Carousels are team-shared; created_by is attribution only.
 *
 * Bodies are snake_case end to end — api.js does not translate, and `data` must
 * reach Postgres untouched.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';

export function useCarousels(enabled = true) {
  return useQuery({
    queryKey: queryKeys.carousels.all,
    queryFn: () => api.get('/carousels?order=updated_at&dir=desc&limit=200'),
    enabled,
    staleTime: 30_000,
  });
}

// Create when there is no id, update when there is. The row's updated_at is sent
// explicitly: the generic CRUD routes do not touch it.
export function useSaveCarousel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => (id
      ? api.put('/carousels/' + id, { ...body, updated_at: new Date().toISOString() })
      : api.post('/carousels', body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.carousels.all }),
  });
}

export function useDeleteCarousel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del('/carousels/' + id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.carousels.all }),
  });
}
